import { useState } from 'react';
import { LoadButton, NoDataMessage, isNoData } from '../shared';
import { RepairsIcon } from '../icons';
import { RepairCard } from '../cards/RepairCard';
import type { UseQueryResult } from '@tanstack/react-query';

interface RepairsTabProps {
  query: UseQueryResult<any>;
  loadEnabled: boolean;
  onLoad: () => void;
  disabled?: boolean;
}

export function RepairsTab({ query, loadEnabled, onLoad, disabled }: RepairsTabProps) {
  const [showAllRepairs, setShowAllRepairs] = useState(false);

  const allRepairs = query.data?.data?.repair || [];
  const displayRepairs = showAllRepairs ? allRepairs : allRepairs.slice(0, 10);

  return (
    <div>
      {!loadEnabled && (
        <div className="flex flex-col items-center justify-center py-12">
          <RepairsIcon className="w-12 h-12 opacity-40 mb-4" />
          <p className="text-gray-400 text-sm mb-4">See common repairs with independent shop and dealer cost estimates</p>
          <LoadButton label="Load Common Repairs" onClick={onLoad} isLoading={false} isLoaded={false} disabled={disabled} />
        </div>
      )}

      {query.isLoading && (
        <p className="text-gray-500 text-sm text-center py-8">Loading common repairs...</p>
      )}

      {query.error && !isNoData(query) && (
        <p className="text-red-400 text-sm text-center py-4">Error loading repair data</p>
      )}

      {isNoData(query) && (
        <NoDataMessage message="No repair data available for this vehicle" />
      )}

      {allRepairs.length > 0 && !isNoData(query) && (
        <div className="space-y-3">
          {displayRepairs.map((repair: any, idx: number) => (
            <RepairCard key={idx} repair={repair} />
          ))}
          {allRepairs.length > 10 && !showAllRepairs && (
            <button
              onClick={() => setShowAllRepairs(true)}
              className="w-full py-3 text-center text-purple-400 hover:text-purple-300 transition"
            >
              Show all ({allRepairs.length}) repairs
            </button>
          )}
          {showAllRepairs && allRepairs.length > 10 && (
            <button
              onClick={() => setShowAllRepairs(false)}
              className="w-full py-3 text-center text-gray-400 hover:text-gray-300 transition"
            >
              Show less
            </button>
          )}
        </div>
      )}
    </div>
  );
}
